// Icon set — drawn here rather than pulled from a package.
//
// Every glyph is on a 24 grid with a 1.6 stroke, round caps and round joins,
// and inherits `currentColor`. That consistency is most of what separates an
// icon set from a pile of icons: mixed stroke weights and mixed fill/outline
// styles at the same level read as sloppy even when nobody can say why.
//
// Emoji are deliberately absent. They are font-dependent, differ per
// platform, cannot be themed, and immediately date an interface.

interface P {
  size?: number;
  className?: string;
}

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false,
});

export const IconOverview = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M3 13h4l3 7 4-16 3 9h4" />
  </svg>
);

export const IconOrders = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M5 3h9l5 5v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
    <path d="M14 3v5h5M8.5 13h7M8.5 17h4" />
  </svg>
);

export const IconBalances = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <rect x="2.5" y="6" width="19" height="13" rx="2.5" />
    <path d="M2.5 10.5h19M6 15h3" />
  </svg>
);

export const IconRecon = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M3 7h11M3 7l3-3M3 7l3 3" />
    <path d="M21 17H10M21 17l-3-3M21 17l-3 3" />
  </svg>
);

export const IconApprovals = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M12 3 4 6v6c0 4.6 3.2 8.4 8 9.5 4.8-1.1 8-4.9 8-9.5V6l-8-3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export const IconPricing = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2a2 2 0 0 1-.6-1.4V4.5A1.5 1.5 0 0 1 4.3 3h7.5a2 2 0 0 1 1.4.6l7.4 7.4a2 2 0 0 1 0 2.4Z" />
    <circle cx="7.5" cy="7.5" r="1.2" />
  </svg>
);

export const IconUsers = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" />
    <circle cx="9" cy="7.5" r="3.5" />
    <path d="M22 20v-1.5a4 4 0 0 0-3-3.87M16.5 4.13a4 4 0 0 1 0 6.74" />
  </svg>
);

export const IconDesk = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </svg>
);

export const IconSun = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

export const IconMoon = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </svg>
);

export const IconSignOut = ({ size = 18, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
);

export const IconCheck = ({ size = 16, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="m4.5 12.5 5 5 10-11" />
  </svg>
);

export const IconAlert = ({ size = 16, className }: P) => (
  <svg {...base(size)} className={className}>
    <circle cx="12" cy="12" r="9.25" />
    <path d="M12 7.5v5.5M12 16.3v.2" />
  </svg>
);

export const IconInbox = ({ size = 20, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M3 13h4l1.5 3h7L17 13h4" />
    <path d="M5.4 5h13.2a2 2 0 0 1 1.9 1.4L22 13v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5l1.5-6.6A2 2 0 0 1 5.4 5Z" />
  </svg>
);

export const IconRefresh = ({ size = 16, className }: P) => (
  <svg {...base(size)} className={className}>
    <path d="M20 11A8 8 0 0 0 6.3 6.3L3 9.5M3 9.5V4M3 9.5h5.5" />
    <path d="M4 13a8 8 0 0 0 13.7 4.7L21 14.5M21 14.5V20M21 14.5h-5.5" />
  </svg>
);
